import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Bot,
  CheckCircle2,
  ClipboardPaste,
  Compass,
  Copy,
  ExternalLink,
  FilePlus2,
  FileText,
  FolderOpen,
  Link2,
  Pencil,
  Save,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react';
import GeometryTool from '../components/tools/GeometryTool';
import GraphSketchTool from '../components/tools/GraphSketchTool';
import logo from '../assets/images/logo.png';
import useMetaTags from '../hooks/useMetaTags';
import { useAuthStore } from '../stores/authStore';
import { useToolsStore } from '../stores/toolsStore';
import {
  canSaveToolWork,
  getCurrentPlan,
  getUserSubscription,
  LAUNCH_DISCOUNT_LIMIT,
  PLAN_IDS,
} from '../utils/subscription';

const TOOL_SECTIONS = {
  AI: 'ai',
  REACT: 'react',
};

const SUBJECT_OPTIONS = ['Mathematics', 'Physical Sciences', 'Natural Sciences'];
const GRADE_OPTIONS = ['4', '5', '6', '7', '8', '9', '10', '11', '12'];

const EMPTY_GEM_FORM = {
  title: '',
  description: '',
  url: '',
  isPublic: false,
  subjects: [],
  grades: [],
};

const BUILT_IN_TOOLS = [
  {
    id: 'geometry',
    title: 'Geosketch Canvas',
    description:
      'A browser-based workspace with drawing, labeling, math input, and export controls.',
    route: '/tools/react/geometry',
    component: GeometryTool,
  },
  {
    id: 'graphsketch',
    title: 'GraphSketch Canvas',
    description:
      'Sketch functions and inverses, plot from tables, transform graphs, and read inequalities.',
    route: '/tools/react/graphsketch',
    component: GraphSketchTool,
  },
];

const DEFAULT_META = {
  title: 'Sigma Classroom',
  description: 'Sigma Classroom - Beautiful mathematical typesetting for mathematics and sciences',
};

const DOCUMENT_TOOLS_ONBOARDING_DISMISSED_KEY = 'sigma.onboarding.toolsLanding.dismissed';
const DOCUMENT_TOOLS_ONBOARDING_SESSION_KEY = 'sigma.onboarding.documentTools.session';
const DOCUMENT_TOOLS_ONBOARDING_CONTEXT_KEY = 'sigma.onboarding.documentTools.context';

const buildAbsoluteUrl = (pathname) => {
  if (typeof window === 'undefined') return pathname;
  return new URL(pathname, window.location.origin).toString();
};

const copyToClipboard = async (value) => {
  if (navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return true;
  }

  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand('copy');
  document.body.removeChild(textarea);
  return copied;
};

const ToolsPage = ({ toolSlug = null, gemId = null }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userData } = useAuthStore();
  const {
    gems,
    sharedGem,
    isLoading: gemsLoading,
    error: gemsError,
    loadGems,
    createGem,
    updateGem,
    deleteGem,
    loadPublicGemById,
    clearSharedGem,
    clearToolsError,
  } = useToolsStore();
  const [activeSection, setActiveSection] = useState(
    gemId ? TOOL_SECTIONS.AI : toolSlug ? TOOL_SECTIONS.REACT : TOOL_SECTIONS.AI
  );
  const [gemForm, setGemForm] = useState(EMPTY_GEM_FORM);
  const [editingGemId, setEditingGemId] = useState(null);
  const [gemSearch, setGemSearch] = useState('');
  const [selectedSubjectFilters, setSelectedSubjectFilters] = useState([]);
  const [selectedGradeFilters, setSelectedGradeFilters] = useState([]);
  const [copiedLink, setCopiedLink] = useState('');
  const [showBuiltInSaveBar, setShowBuiltInSaveBar] = useState(false);
  const [openBuiltInLibrarySignal, setOpenBuiltInLibrarySignal] = useState(0);
  const [newBuiltInCanvasSignal, setNewBuiltInCanvasSignal] = useState(0);
  const [openGraphSchemaImportSignal, setOpenGraphSchemaImportSignal] = useState(0);
  const [isBuiltInHeaderVisible, setIsBuiltInHeaderVisible] = useState(false);
  const [showDocumentOnboarding, setShowDocumentOnboarding] = useState(false);
  const [documentReturnContext, setDocumentReturnContext] = useState(null);

  const selectedBuiltInTool = useMemo(
    () => BUILT_IN_TOOLS.find((tool) => tool.id === toolSlug) || null,
    [toolSlug]
  );

  const SharedToolComponent = selectedBuiltInTool?.component || null;

  const trimmedForm = useMemo(
    () => ({
      title: gemForm.title.trim(),
      description: gemForm.description.trim(),
      url: gemForm.url.trim(),
      isPublic: Boolean(gemForm.isPublic),
      subjects: gemForm.subjects.filter((subject) => SUBJECT_OPTIONS.includes(subject)),
      grades: gemForm.grades.filter((grade) => GRADE_OPTIONS.includes(String(grade))),
    }),
    [gemForm]
  );

  const canAddGem = trimmedForm.title && trimmedForm.description && trimmedForm.url;
  const normalizedGemSearch = gemSearch.trim().toLowerCase();

  const filteredGems = useMemo(() => {
    return gems.filter((gem) => {
      const gemTitle = String(gem.title || '').toLowerCase();
      const gemDescription = String(gem.description || '').toLowerCase();
      const gemSubjects = Array.isArray(gem.subjects) ? gem.subjects : [];
      const gemGrades = Array.isArray(gem.grades) ? gem.grades.map(String) : [];

      const matchesSearch =
        !normalizedGemSearch ||
        gemTitle.includes(normalizedGemSearch) ||
        gemDescription.includes(normalizedGemSearch);
      const matchesSubjects =
        selectedSubjectFilters.length === 0 ||
        selectedSubjectFilters.some((subject) => gemSubjects.includes(subject));
      const matchesGrades =
        selectedGradeFilters.length === 0 ||
        selectedGradeFilters.some((grade) => gemGrades.includes(grade));

      return matchesSearch && matchesSubjects && matchesGrades;
    });
  }, [gems, normalizedGemSearch, selectedGradeFilters, selectedSubjectFilters]);

  const handleGemFieldChange = (field) => (event) => {
    const { value, checked, type } = event.target;
    setGemForm((current) => ({
      ...current,
      [field]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleGemMultiSelectToggle = (field, value) => () => {
    setGemForm((current) => {
      const selectedValues = Array.isArray(current[field]) ? current[field] : [];
      const exists = selectedValues.includes(value);
      return {
        ...current,
        [field]: exists
          ? selectedValues.filter((item) => item !== value)
          : [...selectedValues, value],
      };
    });
  };

  const handleFilterToggle = (field, value) => () => {
    const setFilter =
      field === 'subjects' ? setSelectedSubjectFilters : setSelectedGradeFilters;

    setFilter((current) => {
      const exists = current.includes(value);
      return exists ? current.filter((item) => item !== value) : [...current, value];
    });
  };

  const clearGemFilters = () => {
    setGemSearch('');
    setSelectedSubjectFilters([]);
    setSelectedGradeFilters([]);
  };

  const getDocumentAwareRoute = (path) => {
    if (!documentReturnContext?.returnPath) return path;

    const params = new URLSearchParams({
      from: 'document-editor',
      returnTo: documentReturnContext.returnPath,
    });

    return `${path}?${params.toString()}`;
  };

  const returnToDocument = () => {
    const returnPath = documentReturnContext?.returnPath || '/document/new';
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(DOCUMENT_TOOLS_ONBOARDING_DISMISSED_KEY, 'true');
      window.sessionStorage.removeItem(DOCUMENT_TOOLS_ONBOARDING_SESSION_KEY);
      window.sessionStorage.removeItem(DOCUMENT_TOOLS_ONBOARDING_CONTEXT_KEY);
    }
    navigate(returnPath);
  };

  const handleCopyLink = async (path) => {
    const absoluteUrl = buildAbsoluteUrl(path);

    try {
      await copyToClipboard(absoluteUrl);
      setCopiedLink(absoluteUrl);
      window.setTimeout(() => {
        setCopiedLink((current) => (current === absoluteUrl ? '' : current));
      }, 2000);
    } catch (error) {
      console.error('Failed to copy tool link:', error);
    }
  };

  const handleSubmitGem = (event) => {
    event.preventDefault();

    if (!canAddGem || !user?.uid) return;

    let normalizedUrl = trimmedForm.url;
    if (!/^https?:\/\//i.test(normalizedUrl)) {
      normalizedUrl = `https://${normalizedUrl}`;
    }

    const gemPayload = {
      userId: user.uid,
      title: trimmedForm.title,
      description: trimmedForm.description,
      url: normalizedUrl,
      isPublic: trimmedForm.isPublic,
      subjects: trimmedForm.subjects,
      grades: trimmedForm.grades,
    };

    const request = editingGemId
      ? updateGem(editingGemId, gemPayload)
      : createGem(gemPayload);

    request
      .then(() => {
        setGemForm(EMPTY_GEM_FORM);
        setEditingGemId(null);
      })
      .catch((error) => {
        console.error(
          editingGemId ? 'Failed to update Gemini Gem:' : 'Failed to save Gemini Gem:',
          error
        );
      });
  };

  const handleStartEditGem = (gem) => {
    setGemForm({
      title: gem.title || '',
      description: gem.description || '',
      url: gem.url || '',
      isPublic: Boolean(gem.isPublic),
      subjects: Array.isArray(gem.subjects)
        ? gem.subjects.filter((subject) => SUBJECT_OPTIONS.includes(subject))
        : [],
      grades: Array.isArray(gem.grades)
        ? gem.grades.map(String).filter((grade) => GRADE_OPTIONS.includes(grade))
        : [],
    });
    setEditingGemId(gem.id);
    setActiveSection(TOOL_SECTIONS.AI);
  };

  const handleCancelEditGem = () => {
    setGemForm(EMPTY_GEM_FORM);
    setEditingGemId(null);
  };

  const handleDeleteGem = (id) => {
    if (editingGemId === id) {
      handleCancelEditGem();
    }
    deleteGem(id).catch((error) => {
      console.error('Failed to delete Gemini Gem:', error);
    });
  };

  useEffect(() => {
    if (toolSlug) {
      setActiveSection(TOOL_SECTIONS.REACT);
      return;
    }

    if (gemId) {
      setActiveSection(TOOL_SECTIONS.AI);
      return;
    }

    setActiveSection(TOOL_SECTIONS.AI);
  }, [gemId, toolSlug]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const params = new URLSearchParams(location.search);
    let sessionContext = null;

    try {
      const rawContext = window.sessionStorage.getItem(DOCUMENT_TOOLS_ONBOARDING_CONTEXT_KEY);
      sessionContext = rawContext ? JSON.parse(rawContext) : null;
    } catch (_) {
      sessionContext = null;
    }

    const cameFromDocument =
      params.get('from') === 'document-editor' ||
      window.sessionStorage.getItem(DOCUMENT_TOOLS_ONBOARDING_SESSION_KEY) === 'true';

    if (!cameFromDocument) return;

    const requestedReturnPath = params.get('returnTo') || sessionContext?.returnPath || '/document/new';
    const safeReturnPath = requestedReturnPath.startsWith('/document')
      ? requestedReturnPath
      : '/document/new';

    setDocumentReturnContext({
      returnPath: safeReturnPath,
      documentName: sessionContext?.documentName || 'your document',
    });

    if (!toolSlug && !gemId) {
      window.sessionStorage.removeItem(DOCUMENT_TOOLS_ONBOARDING_SESSION_KEY);
      setActiveSection(TOOL_SECTIONS.REACT);
    }

    if (
      !toolSlug &&
      !gemId &&
      window.localStorage.getItem(DOCUMENT_TOOLS_ONBOARDING_DISMISSED_KEY) !== 'true'
    ) {
      setShowDocumentOnboarding(true);
    }
  }, [gemId, location.search, toolSlug]);

  const dismissDocumentOnboarding = () => {
    setShowDocumentOnboarding(false);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(DOCUMENT_TOOLS_ONBOARDING_DISMISSED_KEY, 'true');
    }
  };

  useEffect(() => {
    if (!user?.uid || gemId) return undefined;

    loadGems(user.uid).catch((error) => {
      console.error('Failed to load Gemini Gems:', error);
    });

    return () => {
      clearToolsError();
    };
  }, [clearToolsError, gemId, loadGems, user?.uid]);

  useEffect(() => {
    if (!gemId) {
      clearSharedGem();
      return undefined;
    }

    loadPublicGemById(gemId).catch((error) => {
      console.error('Failed to load shared Gemini Gem:', error);
    });

    return () => {
      clearSharedGem();
      clearToolsError();
    };
  }, [clearSharedGem, clearToolsError, gemId, loadPublicGemById]);

  const backTarget = documentReturnContext?.returnPath || (user?.uid ? '/document/new' : '/tools');
  const isBuiltInToolView = Boolean(toolSlug && selectedBuiltInTool && SharedToolComponent);
  const isImmersiveBuiltInToolView = isBuiltInToolView && selectedBuiltInTool?.id === 'graphsketch';
  const currentPlan = getCurrentPlan(userData);
  const subscription = getUserSubscription(userData);
  const canSaveBuiltInTools = canSaveToolWork(userData);
  const isTrialPlan = currentPlan.id === PLAN_IDS.FREE;
  const metaTitle = useMemo(() => {
    if (selectedBuiltInTool) {
      return `${selectedBuiltInTool.title} | Sigma Classroom`;
    }

    if (sharedGem?.title) {
      return `${sharedGem.title} | Sigma Classroom`;
    }

    if (gemId) {
      return 'Shared AI Tool | Sigma Classroom';
    }

    return DEFAULT_META.title;
  }, [gemId, selectedBuiltInTool, sharedGem?.title]);

  const metaDescription = useMemo(() => {
    if (selectedBuiltInTool?.description) {
      return selectedBuiltInTool.description;
    }

    if (sharedGem?.description) {
      return sharedGem.description;
    }

    if (gemId) {
      return 'Open this shared AI tool in Sigma Classroom.';
    }

    return DEFAULT_META.description;
  }, [gemId, selectedBuiltInTool?.description, sharedGem?.description]);

  const metaUrl = useMemo(() => {
    if (selectedBuiltInTool?.route) {
      return buildAbsoluteUrl(selectedBuiltInTool.route);
    }

    if (sharedGem?.id) {
      return buildAbsoluteUrl(`/tools/gem/${sharedGem.id}`);
    }

    if (gemId) {
      return buildAbsoluteUrl(`/tools/gem/${gemId}`);
    }

    return buildAbsoluteUrl('/tools');
  }, [gemId, selectedBuiltInTool?.route, sharedGem?.id]);

  useMetaTags(metaTitle, metaDescription, metaUrl);

  const renderBuiltInToolCards = () => (
    <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {BUILT_IN_TOOLS.map((tool) => (
        <article
          key={tool.id}
          className="overflow-hidden rounded-2xl border border-gray-200 bg-white p-6 shadow-sm"
        >
          <p className="inline-flex items-center gap-2 rounded-full bg-sigma-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-sigma-700">
            <Sparkles className="w-3.5 h-3.5" />
            Interactive Tool
          </p>
          <h2 className="mt-3 text-2xl font-bold text-gray-900">{tool.title}</h2>
          <p className="mt-2 text-sm leading-6 text-gray-600">{tool.description}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link
              to={getDocumentAwareRoute(tool.route)}
              className="inline-flex items-center gap-2 rounded-lg bg-sigma-700 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-sigma-800"
            >
              Open shared tool
              <ExternalLink className="w-4 h-4" />
            </Link>
            <button
              type="button"
              onClick={() => handleCopyLink(tool.route)}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
            >
              <Copy className="w-4 h-4" />
              {copiedLink === buildAbsoluteUrl(tool.route) ? 'Copied' : 'Copy share link'}
            </button>
          </div>
        </article>
      ))}
    </section>
  );

  const renderSubscriptionCallout = () => (
    <section className="rounded-3xl border border-slate-200 bg-slate-950 p-6 text-white shadow-xl">
      <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <div className="max-w-3xl">
          <p className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-200">
            <Sparkles className="h-3.5 w-3.5" />
            Subscription
          </p>
          <h2 className="mt-4 text-3xl font-bold tracking-tight">
            Start with 30 days of trial access, then upgrade when teachers need to save.
          </h2>
          <p className="mt-3 text-sm leading-6 text-slate-300">
            Trial access includes 10 saved documents with up to 5 pages each. Basic unlocks saved canvases,
            PDFs, and sharing. Pro adds assessments, manual eMarking, analysis, and learner management.
          </p>
        </div>

        <div className="rounded-2xl border border-amber-300/20 bg-amber-300/10 px-5 py-4">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-100">
            Launch Offer
          </div>
          <div className="mt-2 text-2xl font-bold text-white">50% off yearly</div>
          <div className="mt-1 text-sm text-amber-50/80">
            First {LAUNCH_DISCOUNT_LIMIT} paid subscribers
          </div>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <span className="rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm font-medium text-slate-100">
          Current plan: {currentPlan.name}
        </span>
        {subscription.planId !== PLAN_IDS.FREE && subscription.launchDiscountApplied ? (
          <span className="rounded-full border border-emerald-300/25 bg-emerald-400/10 px-4 py-2 text-sm font-medium text-emerald-200">
            Launch discount applied
          </span>
        ) : null}
        <Link
          to="/pricing"
          className="inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-slate-950 transition hover:bg-slate-100"
        >
          View pricing and checkout
          <ExternalLink className="h-4 w-4" />
        </Link>
      </div>
    </section>
  );

  const renderTrialBanner = (className = '') =>
    isTrialPlan ? (
      <div className={['border-b border-white/10 bg-amber-300/12 px-4 py-1.5 text-xs text-amber-50', className].join(' ')}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span>
            {subscription.status === 'expired'
              ? 'Trial ended. Upgrade to keep using saved work, sharing, and paid features.'
              : `${subscription.trialDaysRemaining || 0} trial day${subscription.trialDaysRemaining === 1 ? '' : 's'} left. Upgrade when you need saved canvases, sharing, and more access.`}
          </span>
          <Link to="/pricing" className="font-semibold text-amber-200 transition hover:text-white">
            Explore plans
          </Link>
        </div>
      </div>
    ) : null;

  const renderDocumentOnboarding = () =>
    showDocumentOnboarding ? (
      <section className="rounded-2xl border border-sigma-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex min-w-0 gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-sigma-700 text-white">
              <FileText className="h-6 w-6" />
            </div>
            <div className="min-w-0">
              <p className="inline-flex items-center gap-2 rounded-full bg-sigma-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-sigma-700">
                <CheckCircle2 className="h-3.5 w-3.5" />
                From Document Editor
              </p>
              <h2 className="mt-3 text-2xl font-bold text-gray-900">Create reusable tool work, then bring it back into your document.</h2>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-gray-600">
                Start with Geosketch or GraphSketch for diagrams and graphs. When you are done, use the document return action to go back to {documentReturnContext?.documentName || 'your document'}.
              </p>
              <div className="mt-4 grid gap-3 sm:grid-cols-3">
                <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                  <div className="text-sm font-semibold text-gray-900">1. Open a tool</div>
                  <p className="mt-1 text-xs leading-5 text-gray-600">Choose a canvas or a saved AI tool link.</p>
                </div>
                <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                  <div className="text-sm font-semibold text-gray-900">2. Build the asset</div>
                  <p className="mt-1 text-xs leading-5 text-gray-600">Sketch, label, graph, or save reusable work.</p>
                </div>
                <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                  <div className="text-sm font-semibold text-gray-900">3. Add to document</div>
                  <p className="mt-1 text-xs leading-5 text-gray-600">Export or copy, then insert it back in the editor.</p>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <Link
                  to={getDocumentAwareRoute('/tools/react/geometry')}
                  className="inline-flex items-center gap-2 rounded-lg bg-sigma-700 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-sigma-800"
                >
                  Open Geosketch
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to={getDocumentAwareRoute('/tools/react/graphsketch')}
                  className="inline-flex items-center gap-2 rounded-lg border border-sigma-200 bg-sigma-50 px-4 py-2 text-sm font-semibold text-sigma-800 transition-colors hover:bg-sigma-100"
                >
                  Open GraphSketch
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <button
                  type="button"
                  onClick={returnToDocument}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
                >
                  Return to Document
                  <FileText className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={dismissDocumentOnboarding}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-200 text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700"
            aria-label="Dismiss document tools onboarding"
            title="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </section>
    ) : null;

  return (
    <div className="min-h-screen bg-gray-100">
      <div
        className={[
          'mx-auto',
          isBuiltInToolView ? 'flex min-h-screen max-w-none flex-col px-0 py-0' : 'max-w-7xl px-4 py-6 sm:px-6',
        ].join(' ')}
      >
        {!isBuiltInToolView && (
          <div className="relative mb-6 overflow-hidden rounded-xl bg-gradient-to-r from-sigma-600 to-sigma-800 p-6">
            {renderTrialBanner('mx-[-1.5rem] mt-[-1.5rem] mb-4 sm:mx-[-1.5rem]')}
            <div className="relative z-10 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => navigate(backTarget)}
                  className="rounded-md p-1.5 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
                <div>
                  <h1 className="flex items-center gap-2 text-2xl font-bold text-white">
                    <Compass className="h-6 w-6" />
                    Tools
                  </h1>
                  <p className="mt-0.5 text-sm text-sigma-100">
                    {gemId ? 'Shared AI tool' : 'Interactive classroom utilities'}
                  </p>
                </div>
              </div>
            </div>
            <div className="absolute -top-6 -right-6 h-32 w-32 rounded-full bg-white/5" />
            <div className="absolute -bottom-8 -left-8 h-40 w-40 rounded-full bg-white/5" />
          </div>
        )}

        <div className={isImmersiveBuiltInToolView ? 'h-screen' : 'space-y-3'}>
          {isBuiltInToolView ? (
            <section className="relative flex h-screen max-h-screen flex-1 flex-col overflow-hidden bg-white">
              <div className="pointer-events-none absolute inset-x-0 top-0 z-50">
                <div className="flex justify-center pt-1">
                  <button
                    type="button"
                    onMouseEnter={() => setIsBuiltInHeaderVisible(true)}
                    onFocus={() => setIsBuiltInHeaderVisible(true)}
                    className="pointer-events-auto h-5 w-28 rounded-full bg-sigma-700/20 backdrop-blur-sm transition-colors hover:bg-sigma-700/30 focus:bg-sigma-700/30 focus:outline-none sm:w-36"
                    aria-label="Show tool header"
                  />
                </div>
                <div
                  onMouseEnter={() => setIsBuiltInHeaderVisible(true)}
                  onMouseLeave={() => setIsBuiltInHeaderVisible(false)}
                  className={[
                    'absolute inset-x-0 top-0 bg-sigma-700/95 text-white shadow-lg backdrop-blur-md transition-all duration-200 ease-out',
                    isBuiltInHeaderVisible ? 'pointer-events-auto translate-y-0 opacity-100' : 'pointer-events-none -translate-y-full opacity-0',
                  ].join(' ')}
                >
                  {renderTrialBanner()}
                  <div className="flex flex-wrap items-start justify-between gap-4 px-4 py-2 sm:px-5">
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => navigate(getDocumentAwareRoute('/tools'))}
                        className="inline-flex h-6 w-6 shrink-0 items-center justify-center text-white transition-colors hover:bg-white/20"
                        aria-label="Back to tools"
                      >
                        <ArrowLeft className="h-5 w-5" />
                      </button>
                      <img src={logo} alt="Sigma" className="h-16 w-auto shrink-0 rounded-md p-1" />
                      <div>
                        <h1 className="text-xl font-bold sm:text-2xl">{selectedBuiltInTool.title}</h1>
                        <p className="max-w-2xl text-sm text-sigma-100">
                        {selectedBuiltInTool.description}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {documentReturnContext?.returnPath && (
                        <button
                          type="button"
                          onClick={returnToDocument}
                          className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white px-3 py-2 text-sm font-semibold text-sigma-700 transition-colors hover:bg-sigma-50"
                        >
                          <FileText className="h-4 w-4" />
                          Back to document
                        </button>
                      )}
                      {!canSaveBuiltInTools && (
                        <Link
                          to="/pricing"
                          className="inline-flex items-center gap-2 rounded-lg border border-amber-200/40 bg-amber-300 px-3 py-2 text-sm font-semibold text-slate-950 transition-colors hover:bg-amber-200"
                        >
                          Upgrade to save
                        </Link>
                      )}
                      {['geometry', 'graphsketch'].includes(selectedBuiltInTool?.id || '') && (
                        <>
                          <button
                            type="button"
                            onClick={() => setNewBuiltInCanvasSignal((current) => current + 1)}
                            className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white px-3 py-2 text-sm font-medium text-sigma-700 transition-colors hover:bg-sigma-50"
                            title="New canvas"
                            aria-label="New canvas"
                          >
                            <FilePlus2 className="w-4 h-4" />
                          </button>
                          {selectedBuiltInTool?.id === 'graphsketch' && (
                            <button
                              type="button"
                              onClick={() => setOpenGraphSchemaImportSignal((current) => current + 1)}
                              className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white px-3 py-2 text-sm font-medium text-sigma-700 transition-colors hover:bg-sigma-50"
                              title="Import graph schema"
                              aria-label="Import graph schema"
                            >
                              <ClipboardPaste className="w-4 h-4" />
                              Import schema
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              if (!canSaveBuiltInTools) {
                                navigate('/pricing');
                                return;
                              }
                              setOpenBuiltInLibrarySignal((current) => current + 1);
                            }}
                            className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white px-3 py-2 text-sm font-medium text-sigma-700 transition-colors hover:bg-sigma-50"
                            title={canSaveBuiltInTools ? 'Open saved items' : 'Upgrade to open saved items'}
                            aria-label="Open saved items"
                          >
                            <FolderOpen className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              if (!canSaveBuiltInTools) {
                                navigate('/pricing');
                                return;
                              }
                              setShowBuiltInSaveBar((current) => !current);
                            }}
                            className={[
                              'inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors',
                              showBuiltInSaveBar
                                ? 'border-white/30 bg-sigma-800 text-white hover:bg-sigma-900'
                                : 'border-white/20 bg-white text-sigma-700 hover:bg-sigma-50',
                            ].join(' ')}
                            title={
                              canSaveBuiltInTools
                                ? (showBuiltInSaveBar ? 'Hide save bar' : 'Show save bar')
                                : 'Upgrade to save canvases and slides'
                            }
                            aria-label={showBuiltInSaveBar ? 'Hide save bar' : 'Show save bar'}
                          >
                            <Save className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      <button
                        type="button"
                        onClick={() => handleCopyLink(selectedBuiltInTool.route)}
                        className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white px-3 py-2 text-sm font-medium text-sigma-700 transition-colors hover:bg-sigma-50"
                      >
                        <Copy className="w-4 h-4" />
                        {copiedLink === buildAbsoluteUrl(selectedBuiltInTool.route)
                          ? 'Copied'
                          : 'Copy share link'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className={isImmersiveBuiltInToolView ? 'min-h-0 flex-1' : 'min-h-0 flex-1 px-3 py-3 sm:px-4 sm:py-4'}>
                <SharedToolComponent
                  fullScreen
                  showSaveBar={['geometry', 'graphsketch'].includes(selectedBuiltInTool?.id || '') ? showBuiltInSaveBar : undefined}
                  openLibrarySignal={openBuiltInLibrarySignal}
                  newCanvasSignal={newBuiltInCanvasSignal}
                  openSchemaImportSignal={
                    selectedBuiltInTool?.id === 'graphsketch'
                      ? openGraphSchemaImportSignal
                      : undefined
                  }
                />
              </div>
            </section>
          ) : gemId ? (
            <section className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
              <div className="border-b border-gray-200 bg-gradient-to-r from-amber-50 via-white to-sky-50 px-6 py-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <p className="inline-flex items-center gap-2 rounded-full bg-white/80 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-sigma-700 shadow-sm">
                      <Link2 className="w-3.5 h-3.5" />
                      Shared AI Tool
                    </p>
                    <h2 className="mt-3 text-2xl font-bold text-gray-900">Open a public Gemini Gem</h2>
                    <p className="mt-1 text-sm text-gray-600">
                      Anyone with this link can open the tool, even without signing in.
                    </p>
                  </div>
                  <Link
                    to="/tools"
                    className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                  >
                    <Compass className="w-4 h-4" />
                    All tools
                  </Link>
                </div>
              </div>

              <div className="p-6">
                {gemsError ? (
                  <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {gemsError}
                  </div>
                ) : gemsLoading ? (
                  <div className="flex min-h-[220px] items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 p-8 text-center">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800">Loading shared Gemini Gem...</h3>
                    </div>
                  </div>
                ) : sharedGem ? (
                  <article className="overflow-hidden rounded-xl border border-sigma-500 bg-white shadow-lg">
                    <div className="flex items-start justify-between gap-3 border-b border-slate-300 bg-slate-100 px-5 py-4">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{sharedGem.title}</h3>
                        <p className="mt-1 text-sm text-gray-600">{sharedGem.description}</p>
                      </div>
                      <span className="inline-flex rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">
                        Public
                      </span>
                    </div>
                    <div className="space-y-4 px-5 py-4">
                      {Array.isArray(sharedGem.subjects) && sharedGem.subjects.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {sharedGem.subjects.map((subject) => (
                            <span
                              key={`${sharedGem.id}-subject-${subject}`}
                              className="inline-flex rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700"
                            >
                              {subject}
                            </span>
                          ))}
                        </div>
                      )}
                      {Array.isArray(sharedGem.grades) && sharedGem.grades.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {sharedGem.grades.map((grade) => (
                            <span
                              key={`${sharedGem.id}-grade-${grade}`}
                              className="inline-flex rounded-full bg-sigma-50 px-2 py-0.5 text-xs font-medium text-sigma-700"
                            >
                              Grade {grade}
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="flex flex-wrap gap-2">
                        <a
                          href={sharedGem.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 rounded-lg bg-sigma-700 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-sigma-800"
                        >
                          Open Gem
                          <ExternalLink className="w-4 h-4" />
                        </a>
                        <button
                          type="button"
                          onClick={() => handleCopyLink(`/tools/gem/${sharedGem.id}`)}
                          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                        >
                          <Copy className="w-4 h-4" />
                          {copiedLink === buildAbsoluteUrl(`/tools/gem/${sharedGem.id}`) ? 'Copied' : 'Copy share link'}
                        </button>
                      </div>
                    </div>
                  </article>
                ) : null}
              </div>
            </section>
          ) : (
            <>
              {renderDocumentOnboarding()}
              {renderBuiltInToolCards()}

              <section className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                <div className="border-b border-gray-200 bg-gradient-to-r from-slate-50 via-white to-sky-50 px-6 py-5">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <p className="inline-flex items-center gap-2 rounded-full bg-white/80 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-sigma-700 shadow-sm">
                        <Compass className="w-3.5 h-3.5" />
                        Tool Categories
                      </p>
                      <h2 className="mt-3 text-2xl font-bold text-gray-900">Choose a tools workspace</h2>
                      <p className="mt-1 text-sm text-gray-600">
                        Signed-in users can manage AI links here, while shared links stay open to anyone.
                      </p>
                    </div>

                    <div className="inline-flex rounded-xl border border-gray-200 bg-gray-100 p-1">
                      <button
                        type="button"
                        onClick={() => setActiveSection(TOOL_SECTIONS.AI)}
                        className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                          activeSection === TOOL_SECTIONS.AI
                            ? 'bg-white text-sigma-800 shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        <Bot className="w-4 h-4" />
                        AI Tools
                      </button>
                      <button
                        type="button"
                        onClick={() => setActiveSection(TOOL_SECTIONS.REACT)}
                        className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                          activeSection === TOOL_SECTIONS.REACT
                            ? 'bg-white text-sigma-800 shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        <Sparkles className="w-4 h-4" />
                        Interactive Tools
                      </button>
                    </div>
                  </div>
                </div>

                {activeSection === TOOL_SECTIONS.AI ? (
                    <div className="space-y-6 p-6">
                      {renderSubscriptionCallout()}
                      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                        <div className="border-b border-gray-200 bg-gradient-to-r from-amber-50 via-white to-sky-50 px-6 py-5">
                          <div className="flex items-start justify-between gap-4 flex-wrap">
                            <div>
                            <p className="inline-flex items-center gap-2 rounded-full bg-white/80 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-sigma-700 shadow-sm">
                              <Sparkles className="w-3.5 h-3.5" />
                              AI Tools
                            </p>
                            <h2 className="mt-3 text-2xl font-bold text-gray-900 flex items-center gap-2">
                              <Bot className="w-6 h-6 text-sigma-700" />
                              Gemini Gems
                            </h2>
                            <p className="mt-1 text-sm text-gray-600">
                              Save Gemini Gem links, then share public ones with a URL that works for unauthenticated visitors.
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 xl:grid-cols-[360px_1fr] gap-6 p-6">
                        <form onSubmit={handleSubmitGem} className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-4">
                          <div>
                            <label htmlFor="gem-title" className="block text-sm font-medium text-gray-700 mb-1">
                              Gem title
                            </label>
                            <input
                              id="gem-title"
                              type="text"
                              value={gemForm.title}
                              onChange={handleGemFieldChange('title')}
                              placeholder="Exam question generator"
                              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-sigma-500 focus:ring-2 focus:ring-sigma-100"
                            />
                          </div>

                          <div>
                            <label htmlFor="gem-description" className="block text-sm font-medium text-gray-700 mb-1">
                              Description
                            </label>
                            <textarea
                              id="gem-description"
                              value={gemForm.description}
                              onChange={handleGemFieldChange('description')}
                              rows={4}
                              placeholder="What this Gem helps you do"
                              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-sigma-500 focus:ring-2 focus:ring-sigma-100"
                            />
                          </div>

                          <div>
                            <label htmlFor="gem-url" className="block text-sm font-medium text-gray-700 mb-1">
                              Gem link
                            </label>
                            <input
                              id="gem-url"
                              type="url"
                              value={gemForm.url}
                              onChange={handleGemFieldChange('url')}
                              placeholder="https://g.co/gemini/share/..."
                              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-sigma-500 focus:ring-2 focus:ring-sigma-100"
                            />
                          </div>

                          <div>
                            <p className="block text-sm font-medium text-gray-700 mb-2">Subjects</p>
                            <div className="flex flex-wrap gap-2">
                              {SUBJECT_OPTIONS.map((subject) => {
                                const isSelected = gemForm.subjects.includes(subject);
                                return (
                                  <button
                                    key={subject}
                                    type="button"
                                    onClick={handleGemMultiSelectToggle('subjects', subject)}
                                    className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                                      isSelected
                                        ? 'border-sigma-600 bg-sigma-100 text-sigma-800'
                                        : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100'
                                    }`}
                                  >
                                    {subject}
                                  </button>
                                );
                              })}
                            </div>
                          </div>

                          <div>
                            <p className="block text-sm font-medium text-gray-700 mb-2">Grades</p>
                            <div className="flex flex-wrap gap-2">
                              {GRADE_OPTIONS.map((grade) => {
                                const isSelected = gemForm.grades.includes(grade);
                                return (
                                  <button
                                    key={grade}
                                    type="button"
                                    onClick={handleGemMultiSelectToggle('grades', grade)}
                                    className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                                      isSelected
                                        ? 'border-sigma-600 bg-sigma-100 text-sigma-800'
                                        : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100'
                                    }`}
                                  >
                                    Grade {grade}
                                  </button>
                                );
                              })}
                            </div>
                          </div>

                          <div className="flex gap-2">
                            <button
                              type="submit"
                              disabled={!canAddGem || !user?.uid}
                              className="inline-flex w-full items-center justify-center rounded-lg bg-sigma-700 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-sigma-800 disabled:cursor-not-allowed disabled:bg-gray-300"
                            >
                              {editingGemId ? 'Update Gem' : 'Save Gem'}
                            </button>
                            {editingGemId && (
                              <button
                                type="button"
                                onClick={handleCancelEditGem}
                                className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-100"
                              >
                                Cancel
                              </button>
                            )}
                          </div>

                          <label
                            htmlFor="gem-public"
                            className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
                          >
                            <span className="text-gray-700">Make this AI tool public</span>
                            <input
                              id="gem-public"
                              type="checkbox"
                              checked={gemForm.isPublic}
                              onChange={handleGemFieldChange('isPublic')}
                              className="h-4 w-4 rounded border-gray-300 text-sigma-700 focus:ring-sigma-500"
                            />
                          </label>

                          {!user?.uid && (
                            <p className="text-sm text-amber-700">
                              Sign in to save Gemini Gems. Public share links still work without signing in.
                            </p>
                          )}
                          {user?.uid && userData?.requiresProfileCompletion === true && (
                            <p className="text-sm text-amber-700">
                              Complete your profile to match the same Firestore permissions used for marking jobs.
                            </p>
                          )}
                        </form>

                        <div className="space-y-4">
                          {gemsError && (
                            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                              {gemsError}
                            </div>
                          )}
                          <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-4 space-y-4">
                            <div>
                              <label htmlFor="gem-search" className="block text-sm font-medium text-gray-700 mb-1">
                                Search AI tools
                              </label>
                              <input
                                id="gem-search"
                                type="text"
                                value={gemSearch}
                                onChange={(event) => setGemSearch(event.target.value)}
                                placeholder="Search by title or description"
                                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-sigma-500 focus:ring-2 focus:ring-sigma-100"
                              />
                            </div>

                            <div>
                              <p className="block text-sm font-medium text-gray-700 mb-2">Filter by subject</p>
                              <div className="flex flex-wrap gap-2">
                                {SUBJECT_OPTIONS.map((subject) => {
                                  const isSelected = selectedSubjectFilters.includes(subject);
                                  return (
                                    <button
                                      key={`filter-subject-${subject}`}
                                      type="button"
                                      onClick={handleFilterToggle('subjects', subject)}
                                      className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                                        isSelected
                                          ? 'border-sigma-600 bg-sigma-100 text-sigma-800'
                                          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100'
                                      }`}
                                    >
                                      {subject}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>

                            <div>
                              <p className="block text-sm font-medium text-gray-700 mb-2">Filter by grade</p>
                              <div className="flex flex-wrap gap-2">
                                {GRADE_OPTIONS.map((grade) => {
                                  const isSelected = selectedGradeFilters.includes(grade);
                                  return (
                                    <button
                                      key={`filter-grade-${grade}`}
                                      type="button"
                                      onClick={handleFilterToggle('grades', grade)}
                                      className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                                        isSelected
                                          ? 'border-sigma-600 bg-sigma-100 text-sigma-800'
                                          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100'
                                      }`}
                                    >
                                      Grade {grade}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>

                            <div className="flex items-center justify-between gap-2 text-xs text-gray-500">
                              <span>
                                Showing {filteredGems.length} of {gems.length} tools
                              </span>
                              <button
                                type="button"
                                onClick={clearGemFilters}
                                className="rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-100"
                              >
                                Clear filters
                              </button>
                            </div>
                          </div>
                          {!user?.uid ? (
                            <div className="flex h-full min-h-[240px] items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 p-8 text-center">
                              <div>
                                <Bot className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                                <h3 className="text-lg font-semibold text-gray-800">Sign in to manage Gemini Gems</h3>
                                <p className="mt-1 text-sm text-gray-500">
                                  Shared Gem links remain public, but creating and editing them still requires an account.
                                </p>
                              </div>
                            </div>
                          ) : gemsLoading ? (
                            <div className="flex h-full min-h-[240px] items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 p-8 text-center">
                              <div>
                                <h3 className="text-lg font-semibold text-gray-800">Loading Gemini Gems...</h3>
                              </div>
                            </div>
                          ) : gems.length === 0 ? (
                            <div className="flex h-full min-h-[240px] items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 p-8 text-center">
                              <div>
                                <Bot className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                                <h3 className="text-lg font-semibold text-gray-800">No Gemini Gems yet</h3>
                                <p className="mt-1 text-sm text-gray-500">
                                  Add your first Gem link to build a quick-launch AI tools list for the classroom.
                                </p>
                              </div>
                            </div>
                          ) : filteredGems.length === 0 ? (
                            <div className="flex h-full min-h-[240px] items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 p-8 text-center">
                              <div>
                                <Bot className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                                <h3 className="text-lg font-semibold text-gray-800">No tools match these filters</h3>
                                <p className="mt-1 text-sm text-gray-500">
                                  Try a different search term or clear selected subject and grade filters.
                                </p>
                              </div>
                            </div>
                          ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {filteredGems.map((gem) => (
                                <article
                                  key={gem.id}
                                  className="group overflow-hidden rounded-xl border border-sigma-500 bg-white shadow-lg transition-all hover:-translate-y-0.5 hover:shadow-xl"
                                >
                                  <div className="flex items-start justify-between gap-3 border-b border-slate-300 bg-slate-100 px-5 py-4">
                                    <h3 className="text-lg font-semibold text-gray-900">{gem.title}</h3>
                                    <div className="flex items-center gap-1">
                                      <button
                                        type="button"
                                        onClick={() => handleStartEditGem(gem)}
                                        className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-sky-50 hover:text-sky-700"
                                        title="Edit Gem"
                                      >
                                        <Pencil className="w-4 h-4" />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleDeleteGem(gem.id)}
                                        className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
                                        title="Delete Gem"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    </div>
                                  </div>
                                  <div className="px-5 py-4">
                                    <p>
                                      <span
                                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
                                          gem.isPublic
                                            ? 'bg-emerald-100 text-emerald-700'
                                            : 'bg-gray-100 text-gray-600'
                                        }`}
                                      >
                                        {gem.isPublic ? 'Public' : 'Private'}
                                      </span>
                                    </p>
                                    <p className="mt-2 text-sm leading-6 text-gray-600">{gem.description}</p>
                                    {Array.isArray(gem.subjects) && gem.subjects.length > 0 && (
                                      <div className="mt-3 flex flex-wrap gap-2">
                                        {gem.subjects.map((subject) => (
                                          <span
                                            key={`${gem.id}-subject-${subject}`}
                                            className="inline-flex rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700"
                                          >
                                            {subject}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                    {Array.isArray(gem.grades) && gem.grades.length > 0 && (
                                      <div className="mt-2 flex flex-wrap gap-2">
                                        {gem.grades.map((grade) => (
                                          <span
                                            key={`${gem.id}-grade-${grade}`}
                                            className="inline-flex rounded-full bg-sigma-50 px-2 py-0.5 text-xs font-medium text-sigma-700"
                                          >
                                            Grade {grade}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                    <div className="mt-4 flex flex-wrap gap-2">
                                      <a
                                        href={gem.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 rounded-lg bg-sigma-50 px-3 py-2 text-sm font-medium text-sigma-800 transition-colors hover:bg-sigma-100"
                                      >
                                        Open Gem
                                        <ExternalLink className="w-4 h-4" />
                                      </a>
                                      {gem.isPublic && (
                                        <button
                                          type="button"
                                          onClick={() => handleCopyLink(`/tools/gem/${gem.id}`)}
                                          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                                        >
                                          <Copy className="w-4 h-4" />
                                          {copiedLink === buildAbsoluteUrl(`/tools/gem/${gem.id}`)
                                            ? 'Copied'
                                            : 'Copy share link'}
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </article>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6 p-6">
                    <div className="rounded-2xl border border-gray-200 bg-gradient-to-r from-sigma-50 to-white p-6">
                      <p className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-sigma-700 shadow-sm">
                        <Sparkles className="w-3.5 h-3.5" />
                        Interactive Tools
                      </p>
                      <h2 className="mt-3 text-2xl font-bold text-gray-900">Interactive utilities</h2>
                      <p className="mt-1 text-sm text-gray-600">
                        Open or share dedicated public tool links without loading the full workspace here.
                      </p>
                    </div>

                    {renderSubscriptionCallout()}
                    {renderBuiltInToolCards()}
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ToolsPage;
